/**
 * A region's workshops as a calendar over the range its state asks for, with a
 * note when the fetch fails or the region has nothing on. The page owns the
 * state, so this and the phone's list filter the same way.
 */
import { toWorkshopChips } from '../../lib/workshop-chips';
import { EMPTY_WITH_HIDDEN_SUBREGIONS } from '../../primitives/subregion-filter';
import { SubregionLegend } from '../../primitives/SubregionLegend';
import { SubregionPill } from '../../primitives/SubregionPill';
import type { CalendarState } from '../../primitives/use-calendar-state';
import { useSubregionFilter } from '../../primitives/use-subregion-filter';
import type { RenderWorkshopSlot } from '../../primitives/workshops-calendar-render-context';
import { WorkshopsCalendar } from '../../primitives/WorkshopsCalendar';
import type { CalendarView, DayClickHandler } from '../../primitives/workshopsCalendarTypes';

import { useRegionWorkshops } from './overview-api';
import { NOTE, useOpenWorkshop } from './region-workshops-shared';

export interface RegionWorkshopsCalendarProps {
  /** The region's full ancestor path. */
  path: string[];
  calendar: CalendarState;
  views: CalendarView[];
  renderWorkshop?: RenderWorkshopSlot;
  onDayClick?: DayClickHandler;
}

export function RegionWorkshopsCalendar({
  path,
  calendar,
  views,
  renderWorkshop,
  onDayClick,
}: RegionWorkshopsCalendarProps) {
  const openWorkshop = useOpenWorkshop();
  const { data, isLoading, isError } = useRegionWorkshops(
    path,
    calendar.range.from,
    calendar.range.to,
  );

  const workshops = data ?? [];
  const filter = useSubregionFilter(toWorkshopChips(workshops), calendar.hidden);
  // Only speak to emptiness once a real, successful answer has arrived —
  // otherwise a slow network reads as a region with nothing on.
  const isEmpty = !isLoading && !isError && workshops.length === 0;

  return (
    <>
      <WorkshopsCalendar
        workshops={filter.shown}
        title="Workshops"
        views={views}
        view={calendar.view}
        onViewChange={calendar.setView}
        anchor={calendar.anchor}
        onNav={calendar.nav}
        showCancelled={calendar.showCancelled}
        onToggleCancelled={calendar.toggleCancelled}
        filters={
          <SubregionLegend>
            {filter.subregions.map((s) => (
              <SubregionPill
                key={s.label}
                label={s.label}
                color={s.color}
                hidden={filter.hidden.has(s.label)}
                onToggle={() => calendar.toggleSubregion(s.label)}
              />
            ))}
          </SubregionLegend>
        }
        emptyLabel={filter.hidden.size > 0 ? EMPTY_WITH_HIDDEN_SUBREGIONS : undefined}
        showPacing
        highlightMineInGrid={false}
        onWorkshopClick={openWorkshop}
        renderWorkshop={renderWorkshop}
        onDayClick={onDayClick}
      />
      {isError && <p style={NOTE}>Workshops could not be loaded. Try again in a moment.</p>}
      {isEmpty && <p style={NOTE}>No workshops scheduled in this period.</p>}
    </>
  );
}
